import {
  QuoteItemsAttributesPatchResponse,
  QuoteGetResponse,
  QuoteItemsAttributesPatchRequestBody,
  QuoteItemsSearchResponse,
} from '@lio/api-types';
import { quoteItemQueryKey } from '@services/quoteItem/keys';
import { quoteFormQueryKeys } from '@services/quoteForm/keys';
import { formVersionQueryKeys } from '@services/formVersion/keys';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';

type QuoteItemAttributes = {
  quoteId: string;
  quoteItems: QuoteItemsAttributesPatchRequestBody['quoteItems'];
};

export const useUpdateQuoteItemAttributes = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationKey: quoteItemQueryKey.updateQuoteItemAttributesMutationKey,
    mutationFn: async ({ quoteId, quoteItems }: QuoteItemAttributes & { shouldInvalidateQueries?: boolean }) => {
      const data = await client
        .patch(`quote/${quoteId}/quote-item-attributes`, {
          json: {
            quoteItems,
          },
          timeout: 30000,
        })
        .json<QuoteItemsAttributesPatchResponse>();

      return data;
    },
    onSuccess: async (data, variables) => {
      const shouldInvalidateQueries = variables.shouldInvalidateQueries === false ? false : true;

      if (shouldInvalidateQueries) {
        const updatedQuoteItemsById = Object.fromEntries(data.quoteItems.map((qi) => [qi.id, qi]));
        const deletedQuoteItemIds = data.deletedQuoteItemIds || [];
        queryClient.setQueryData<QuoteGetResponse>(['quote', { quoteId: variables.quoteId }], (previousQuote: any) => {
          return previousQuote
            ? {
                ...previousQuote,
                status: data.status,
                termPremium: data.termPremium,
                totalPremium: data.totalPremium,
              }
            : previousQuote;
        });
        const cachedQuoteItemSearch = queryClient.getQueryData<QuoteItemsSearchResponse>(
          quoteItemQueryKey.searchWithParams({
            quoteId: variables.quoteId,
          }),
        );
        const existingQuoteItemIds = cachedQuoteItemSearch?.items.map((qi) => qi.id) ?? [];
        const hasNewQuoteItems = data.quoteItems.some((qi) => !existingQuoteItemIds.includes(qi.id));

        if (hasNewQuoteItems) {
          // The attributes-patch response only carries id/instanceNumber/quoteItemAttributes for each quote item
          // (it never includes quoteItemTypeId, parentId, etc.), so a quote item that's new to the cache can't be
          // merged in directly - refetch to get the fully-shaped item instead.
          await queryClient.invalidateQueries({
            queryKey: quoteItemQueryKey.searchWithParams({
              quoteId: variables.quoteId,
            }),
            refetchType: 'all',
          });
        } else {
          await queryClient.setQueryData<QuoteItemsSearchResponse>(
            quoteItemQueryKey.searchWithParams({
              quoteId: variables.quoteId,
            }),
            (previousQuoteItemSearch) => {
              return previousQuoteItemSearch
                ? ({
                    ...previousQuoteItemSearch,
                    items: previousQuoteItemSearch?.items
                      .filter((qi0) => !deletedQuoteItemIds.includes(qi0.id))
                      .map((qi) => {
                        if (!updatedQuoteItemsById[qi.id]) {
                          return qi;
                        }
                        const updatedQuoteItem = updatedQuoteItemsById[qi.id];
                        const updatedAttributes = qi.quoteItemAttributes.map((qia) => {
                          const updatedAttribute = updatedQuoteItem.quoteItemAttributes.find(
                            (newQia) => newQia.id === qia.id,
                          );
                          if (updatedAttribute) {
                            return updatedAttribute;
                          }
                          return qia;
                        });
                        const updatedAndNewAttributes = updatedAttributes.concat(
                          updatedQuoteItem.quoteItemAttributes.filter(
                            (newQia) => !qi.quoteItemAttributes.map((qia) => qia.id).includes(newQia.id || ''),
                          ),
                        );
                        return {
                          ...qi,
                          quoteItemAttributes: updatedAndNewAttributes,
                        };
                      }),
                  } as QuoteItemsSearchResponse)
                : previousQuoteItemSearch; // cannot update if not there, should not happen when this is called as it had to be set to be able to patch the attributes
            },
          );
        }

        // Attribute changes (e.g. LOIS) can affect which forms apply and which versions are available
        queryClient.invalidateQueries({
          queryKey: quoteFormQueryKeys.get(variables.quoteId),
        });
        queryClient.invalidateQueries({
          queryKey: formVersionQueryKeys.get(variables.quoteId),
        });
      }
    },
    onError: async (error: any) => {
      const data = await error?.response?.json();
      throw Error(data.error.message);
    },
  });
  return { ...result, updateQuoteItemAttributesAsync: result.mutateAsync };
};
